import type { BlockGrid, BlockSlot } from '@/core/time/blockGrid'
import type { BlockyTimeDB } from '@/db/schema'
import {
  entriesForDayRange,
  erase,
  upsert,
  type ChangeSet,
  type EntryContent,
} from '@/core/repository/entryRepository'
import type { Selection } from './markingService'
import { selectionSlots } from './markingService'

/**
 * Copy, cut and paste over ranges of blocks.
 *
 * A clipboard entry is a SEQUENCE, including its gaps. Copying 09:00-12:00
 * where 10:00 is empty and pasting it elsewhere reproduces that hole, because
 * anything else makes paste unpredictable: you would be unable to tell, from
 * looking at the source, what the destination will become.
 */

/** `null` marks a slot that was empty when copied. */
export type ClipboardItem = EntryContent | null

export interface Clipboard {
  items: ClipboardItem[]
  /** Where it came from, so a cut can be undone as one operation. */
  cutFrom: BlockSlot[] | null
}

export async function copySelection(
  grid: BlockGrid,
  selection: Selection,
  db?: BlockyTimeDB,
): Promise<Clipboard> {
  const slots = selectionSlots(grid, selection)
  return { items: await readSlots(slots, db), cutFrom: null }
}

export async function cutSelection(
  grid: BlockGrid,
  selection: Selection,
  db?: BlockyTimeDB,
): Promise<{ clipboard: Clipboard; change: ChangeSet }> {
  const slots = selectionSlots(grid, selection)
  const items = await readSlots(slots, db)
  const change = await erase(slots, db)
  return { clipboard: { items, cutFrom: slots }, change }
}

/**
 * Writes the clipboard starting at `anchor`.
 *
 * Runs off the end of the window rather than wrapping: a paste that would
 * extend past the last day simply stops, which is less surprising than silently
 * landing somewhere unrelated.
 */
export async function pasteAt(
  grid: BlockGrid,
  clipboard: Clipboard,
  anchor: BlockSlot,
  db?: BlockyTimeDB,
): Promise<ChangeSet> {
  const combined: ChangeSet = { before: [], after: [] }
  if (clipboard.items.length === 0) return combined

  let target: BlockSlot = anchor

  for (const item of clipboard.items) {
    if (!grid.isValidSlot(target)) break

    const change = item
      ? await upsert(grid, item, [target], {}, db)
      : // A gap in the copied range clears the destination, so the paste is a
        // faithful reproduction rather than a partial overlay.
        await erase([target], db)

    combined.before.push(...change.before)
    combined.after.push(...change.after)

    const next = grid.addSlots(target, 1)
    if (next.dayKey === target.dayKey && next.slotIndex === target.slotIndex) break
    target = next
  }

  return combined
}

/** Number of slots a paste would occupy. */
export function clipboardSize(clipboard: Clipboard | null): number {
  return clipboard?.items.length ?? 0
}

async function readSlots(
  slots: readonly BlockSlot[],
  db?: BlockyTimeDB,
): Promise<ClipboardItem[]> {
  if (slots.length === 0) return []

  const first = slots[0]
  const last = slots[slots.length - 1]
  if (!first || !last) return []

  // One range query rather than one per slot: a long selection would otherwise
  // be hundreds of round trips to IndexedDB.
  const rows = await entriesForDayRange(first.dayKey, last.dayKey, db)

  const bySlot = new Map<string, EntryContent>()
  for (const row of rows) {
    bySlot.set(`${row.dayKey}:${row.slotIndex}`, {
      eventTypeId: row.eventTypeId,
      eventObjectId: row.eventObjectId,
      comment: row.comment,
      isPlanned: row.isPlanned === 1,
    })
  }

  return slots.map((slot) => bySlot.get(`${slot.dayKey}:${slot.slotIndex}`) ?? null)
}
